import { useQuery } from '@tanstack/react-query';
import { MAX_ATTACHMENT_SIZE_BYTES } from '@wix/ai-scheduling-assistant-constants';
import { DocSourceMissingError, DocTooLargeError, readBoundedBytes } from './doc-content';
import { queryKeys } from './query-client';
import { PROXY_TOO_LARGE_STATUS, fetchRemoteDocument } from './remote-document';

// The byte twin of `lib/doc-content.ts`: fetches a document's raw BYTES rather than its decoded text, for
// the one kind whose payload is binary — a PDF. It shares `readBoundedBytes`, so the cap check, the
// stream `cancel()` and the refuse-never-truncate rule are literally the same code.
//
// WHY BYTES AT ALL, when the `<embed>` could just take the remote URL: a `blob:` URL carries **no
// `Content-Disposition`**. WixMP decides that header for us and we cannot change it, so pointing an embed at
// the signed URL means the browser may download the file instead of rendering it — the exact behaviour this
// feature exists to replace. Fetching the bytes and setting `type: 'application/pdf'` ourselves is what makes
// the preview deterministic. It also keeps the signed URL out of the DOM.
//
// The request itself goes through `fetchRemoteDocument`, which carries those two load-bearing options
// (`credentials: 'omit'`, `referrerPolicy: 'no-referrer'`) and falls back to the pod when the media host
// refuses the browser outright — which `links.wixel.com` does, on the 302 rather than on the bytes. See
// `lib/remote-document.ts`; without it a Wixel-generated PDF cannot be previewed or downloaded at all.

/** The PDF preview cap: **20 MB**, deliberately NOT the 2 MB `MAX_DOC_PREVIEW_BYTES`.
 *
 *  That number's own docstring gives its reason — "no markdown renderer here is virtualised" — and the reason
 *  does not hold for a PDF: the browser's built-in viewer paginates and renders lazily, so a large PDF costs
 *  memory but not a multi-minute layout. Reusing 2 MB would show "too large, download it" for a large share
 *  of ordinary real-world PDFs, i.e. the feature would look broken for the exact file type it was added for.
 *
 *  It is still a cap, and it still REFUSES rather than truncates (half a PDF is not a document). 20 MB is
 *  four times the 5 MB model-inline threshold (`MAX_ATTACHMENT_SIZE_BYTES`). Uploads now go up to 200 MB,
 *  so a bigger PDF gets the viewer's download hand-off instead of a preview.
 *
 *  When the native port lands this becomes a three-surface number, like `DOCUMENT_MAX_BYTES`. */
export const MAX_PDF_PREVIEW_BYTES = 4 * MAX_ATTACHMENT_SIZE_BYTES;

export const PDF_MIME = 'application/pdf';

/** Downloads `url` and returns it as a PDF `Blob`, refusing over `maxBytes`. */
export async function fetchDocBlob(url: string, maxBytes: number = MAX_PDF_PREVIEW_BYTES, type: string = PDF_MIME): Promise<Blob> {
  const res = await fetchRemoteDocument(url);
  // Same mapping `fetchDocText` makes: the pod's 413 is the bounded read's `DocTooLargeError` seen from the
  // other path, and `PdfDocView` keys its no-retry download hand-off on that class.
  if (res.status === PROXY_TOO_LARGE_STATUS) throw new DocTooLargeError(maxBytes, maxBytes);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  // Refuse before downloading when the host advertises the size. It can be absent (chunked), hidden (no CORS
  // expose) or a lie — hence the bounded read, which is the real guard.
  const advertised = Number(res.headers.get('content-length'));
  if (Number.isFinite(advertised) && advertised > maxBytes) throw new DocTooLargeError(advertised, maxBytes);

  const bytes = await readBoundedBytes(res, maxBytes);
  // `bytes.buffer` would hand over the whole backing ArrayBuffer; the view is what we actually read.
  return new Blob([bytes], { type });
}

/** The React Query options for a document's bytes — extracted so the null-URL guard is testable without a
 *  renderer, exactly as `docTextQueryOptions` is. `refetch()` in v4 is NOT gated on `enabled`, so a Retry
 *  over a null source would otherwise `fetch(null)`, i.e. request the relative path `/null`. */
export function docBlobQueryOptions(url: string | null) {
  return {
    queryKey: queryKeys.docBlob(url ?? ''),
    queryFn: () => (url ? fetchDocBlob(url) : Promise.reject(new DocSourceMissingError())),
    enabled: !!url,
  };
}

/** A document's bytes as a Blob, or an idle query when there is no remote source. `retry: false` is the
 *  app-wide default, so the viewer's explicit Retry button is the only recovery. */
export function useDocBlob(url: string | null) {
  return useQuery(docBlobQueryOptions(url));
}
